import * as React from 'react';
import type { YMaps } from '@cian/frontend-ymaps-components/es/types/ymaps';

import { ICoordinates, INewbuildingSummaryCellValue } from '../../types/newbuildingsCompare';
import {
  IDistrictFuture,
  IDistrictTaRating,
  IDistrictTransportAccessibility,
  IInfrastructurePoi,
  TDistrictMapMode,
  TInfrastructurePoiType,
  TTaCategory,
  TTaTab,
} from '../../types/infrastructure';
import { MAP_MARGIN, TMapPlatform } from '../../constants/map';
import { DistrictInfrastructureMap } from '../DistrictInfrastructureMap';
import {
  INFRASTRUCTURE_POI_TYPES_ORDER,
  TA_INSIDE_ORDER,
  TA_OUTSIDE_ORDER,
  TA_ZOOM,
} from '../DistrictInfrastructureMap/constants';
import { FutureYearPanel } from '../DistrictInfrastructureMap/parts/FutureYearPanel';
import { MapServices } from '../DistrictInfrastructureMap/parts/MapServices';
import { TA_SIDEBAR_WIDTH, TaPanel, getTaTabs } from '../DistrictInfrastructureMap/parts/TaPanel';
import { TaRatingModal } from '../DistrictInfrastructureMap/parts/TaRatingModal';
import { TypeFilters } from '../DistrictInfrastructureMap/parts/TypeFilters';
import { MapModal } from '../MapModal';
import { MapModalHeader } from '../MapModal/parts';
import { DistrictInfrastructureMapHeaderContainer } from '../../containers/DistrictInfrastructureMapHeader';
import { getFutureYears } from '../../utils/infrastructure/mapFutureData';
import { toLngLat } from '../../utils/map/getMapLocation';

export interface IDistrictInfrastructureMapModalProps {
  apiKey: string;
  center: ICoordinates;
  future?: IDistrictFuture | null;
  /** Данные ЖК для кнопок контактов в шапке. */
  newbuilding?: INewbuildingSummaryCellValue;
  platform?: TMapPlatform;
  pois: IInfrastructurePoi[];
  polygon?: ICoordinates[];
  taRating?: IDistrictTaRating | null;
  title: string;
  transportAccessibility?: IDistrictTransportAccessibility | null;
  onClose(): void;
  onFutureYearChange?(year: number): void;
  onModeChange?(mode: TDistrictMapMode): void;
  onTypeClick?(type: TInfrastructurePoiType): void;
  onTaRatingOpen?(tab: TTaTab): void;
  onTaSourceClick?(): void;
  onTaTabChange?(tab: TTaTab): void;
}

function getFirstCategory(
  transportAccessibility: IDistrictTransportAccessibility | null | undefined,
  tab: TTaTab,
): TTaCategory | null {
  const groups = (tab === 'inside' ? transportAccessibility?.inside : transportAccessibility?.outside) ?? {};
  const order = tab === 'inside' ? TA_INSIDE_ORDER : TA_OUTSIDE_ORDER;

  return order.find(category => groups[category]?.length) ?? null;
}

export function DistrictInfrastructureMapModal({
  apiKey,
  center,
  future,
  newbuilding,
  platform = 'desktop',
  pois,
  polygon,
  taRating,
  title,
  transportAccessibility,
  onClose,
  onFutureYearChange,
  onModeChange,
  onTaRatingOpen,
  onTypeClick,
  onTaSourceClick,
  onTaTabChange,
}: IDistrictInfrastructureMapModalProps): React.JSX.Element {
  const currentYear = new Date().getFullYear();
  const futureYears = React.useMemo(() => getFutureYears(currentYear), [currentYear]);
  const taTabs = React.useMemo(
    () => (transportAccessibility ? getTaTabs(transportAccessibility) : []),
    [transportAccessibility],
  );

  const [mode, setMode] = React.useState<TDistrictMapMode>('default');
  const [selectedYear, setSelectedYear] = React.useState(currentYear);
  const [activeTaTab, setActiveTaTab] = React.useState<TTaTab>(taTabs[0] ?? 'outside');
  const [activeTaCategory, setActiveTaCategory] = React.useState<TTaCategory | null>(null);
  const [isRatingOpened, setRatingOpened] = React.useState(false);

  // Чипсы показываем только для типов, которые реально есть среди загруженных точек, в фиксированном порядке.
  // В режиме будущего добавляются типы строящихся объектов.
  const availableTypes = React.useMemo(() => {
    const futureTypes = mode === 'future' ? (future?.pois ?? []) : [];

    return INFRASTRUCTURE_POI_TYPES_ORDER.filter(
      type => pois.some(poi => poi.type === type) || futureTypes.some(poi => poi.type === type),
    );
  }, [mode, future, pois]);
  const [activeTypes, setActiveTypes] = React.useState<TInfrastructurePoiType[]>(availableTypes);

  // Точки могут догрузиться уже после открытия модалки — тогда включаем все появившиеся типы.
  React.useEffect(() => {
    setActiveTypes(availableTypes);
  }, [availableTypes]);

  const handleModeChange = React.useCallback(
    (nextMode: TDistrictMapMode) => {
      setMode(nextMode);

      if (nextMode === 'future') {
        setSelectedYear(currentYear);
      }
      if (nextMode === 'ta') {
        const tab = taTabs[0] ?? 'outside';

        setActiveTaTab(tab);
        setActiveTaCategory(getFirstCategory(transportAccessibility, tab));
      }

      onModeChange?.(nextMode);
    },
    [currentYear, taTabs, transportAccessibility, onModeChange],
  );

  const handleServiceClose = React.useCallback(() => {
    setMode('default');
    onModeChange?.('default');
  }, [onModeChange]);

  const handleYearChange = React.useCallback(
    (year: number) => {
      setSelectedYear(year);
      onFutureYearChange?.(year);
    },
    [onFutureYearChange],
  );

  const handleTaTabChange = React.useCallback(
    (tab: TTaTab) => {
      setActiveTaTab(tab);
      setActiveTaCategory(getFirstCategory(transportAccessibility, tab));
      onTaTabChange?.(tab);
    },
    [transportAccessibility, onTaTabChange],
  );

  const handleRatingOpen = React.useCallback(() => {
    setRatingOpened(true);
    onTaRatingOpen?.(activeTaTab);
  }, [activeTaTab, onTaRatingOpen]);

  const visiblePois = React.useMemo(() => pois.filter(poi => activeTypes.includes(poi.type)), [activeTypes, pois]);

  // Строящиеся объекты фильтруются и по году, и по чипсам типов; дороги в режиме будущего видны всегда.
  const visibleFuturePois = React.useMemo(() => {
    if (mode !== 'future' || !future) {
      return undefined;
    }

    return future.pois.filter(poi => poi.constructionYearFrom <= selectedYear && activeTypes.includes(poi.type));
  }, [mode, future, selectedYear, activeTypes]);

  // Зелёная метка на чипсе типа, у которого выбранный год добавляет новые объекты
  // (для первого года таймлайна — всё, что уже строится).
  const badgeTypes = React.useMemo(() => {
    if (mode !== 'future' || !future) {
      return undefined;
    }

    return INFRASTRUCTURE_POI_TYPES_ORDER.filter(type =>
      future.pois.some(
        poi =>
          poi.type === type &&
          (selectedYear === currentYear
            ? poi.constructionYearFrom <= currentYear
            : poi.constructionYearFrom === selectedYear),
      ),
    );
  }, [mode, future, selectedYear, currentYear]);

  const taPois = React.useMemo(() => {
    if (mode !== 'ta' || !transportAccessibility || !activeTaCategory) {
      return null;
    }

    const groups = (activeTaTab === 'inside' ? transportAccessibility.inside : transportAccessibility.outside) ?? {};

    return { category: activeTaCategory, items: groups[activeTaCategory] ?? [] };
  }, [mode, transportAccessibility, activeTaTab, activeTaCategory]);

  const locationOverride = React.useMemo<YMaps.YMapLocationRequest | undefined>(() => {
    if (mode !== 'ta') {
      return undefined;
    }

    return { center: toLngLat(center), zoom: TA_ZOOM[activeTaTab], duration: 300 };
  }, [mode, center, activeTaTab]);

  // Сайдбар перекрывает левый край карты — резервируем под него поле, как ignoredAreas в map-search.
  const marginOverride = React.useMemo<YMaps.Margin | undefined>(() => {
    if (mode !== 'ta' || platform !== 'desktop') {
      return undefined;
    }

    const [top, right, bottom, left] = MAP_MARGIN.desktop;

    return [top, right, bottom, left + TA_SIDEBAR_WIDTH];
  }, [mode, platform]);

  return (
    <MapModal
      closeTestId="district-infrastructure-map-close"
      header={
        // Кнопки контактов показываем только на десктопе — как в полноэкранной галерее.
        platform === 'desktop' ? (
          <DistrictInfrastructureMapHeaderContainer newbuilding={newbuilding} title={title} />
        ) : (
          <MapModalHeader testId="district-infrastructure-map-header" title={title} />
        )
      }
      testId="district-infrastructure-map-modal"
      onClose={onClose}
    >
      <DistrictInfrastructureMap
        apiKey={apiKey}
        center={center}
        futurePois={visibleFuturePois}
        futureRoads={mode === 'future' ? future?.roads : undefined}
        locationOverride={locationOverride}
        marginOverride={marginOverride}
        platform={platform}
        pois={mode === 'ta' ? [] : visiblePois}
        polygon={polygon}
        taPois={taPois}
        title={title}
      />
      {/* На мобильной карте панели «Дополнительно» нет — как в map-search. */}
      {mode === 'default' && platform === 'desktop' && (
        <MapServices
          hasFuture={Boolean(future)}
          hasTransportAccessibility={taTabs.length > 0}
          onSelect={handleModeChange}
        />
      )}
      {mode !== 'ta' && (
        <TypeFilters
          activeTypes={activeTypes}
          badgeTypes={badgeTypes}
          types={availableTypes}
          onChange={setActiveTypes}
          onTypeClick={onTypeClick}
        />
      )}
      {mode === 'future' && (
        <FutureYearPanel
          selectedYear={selectedYear}
          years={futureYears}
          onChange={handleYearChange}
          onClose={handleServiceClose}
        />
      )}
      {mode === 'ta' && transportAccessibility && (
        <TaPanel
          activeCategory={activeTaCategory}
          activeTab={activeTaTab}
          data={transportAccessibility}
          rating={taRating ?? null}
          onCategoryChange={setActiveTaCategory}
          onClose={handleServiceClose}
          onRatingOpen={handleRatingOpen}
          onSourceClick={() => onTaSourceClick?.()}
          onTabChange={handleTaTabChange}
        />
      )}
      {isRatingOpened && taRating && (
        <TaRatingModal
          rating={taRating}
          tab={activeTaTab}
          onClose={() => setRatingOpened(false)}
          onSourceClick={() => onTaSourceClick?.()}
        />
      )}
    </MapModal>
  );
}
